// components/Orders/OrderDetailsModal.js
import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Spinner } from "reactstrap";
import { orderApiService } from "services/orderApiService";

const OrderDetailsModal = ({ isOpen, toggle, orderId }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchOrderDetails();
    }
  }, [isOpen, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const data = await orderApiService.getOrder(orderId);
      setOrder(data);
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  const getPaymentMethodBadge = (method) => {
    const methods = {
      Cash_on_delivery: { color: "success", text: "Cash on Delivery" },
      online_payment: { color: "primary", text: "Online Payment" },
      card: { color: "info", text: "Card Payment" },
    };
    const config = methods[method] || { color: "secondary", text: method };
    return <span className={`badge bg-${config.color}`}>{config.text}</span>;
  };

  const formatCurrency = (amount) => {
    return `৳${parseFloat(amount || 0).toLocaleString('en-BD')}`;
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>Order Details</ModalHeader>
        <ModalBody className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading order details...</p>
        </ModalBody>
      </Modal>
    );
  }

  if (!order) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Order Details - #{order.id}
        <div className="mt-2">
          <small className="text-muted">
            {order.orderdate} at {order.ordertime}
          </small>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="row">
          <div className="col-md-6">
            <h6 className="mb-3">Customer Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td className="text-muted" width="40%">Customer Name:</td>
                  <td className="fw-semibold">{order.username}</td>
                </tr>
                <tr>
                  <td className="text-muted">Phone:</td>
                  <td>{order.phonenumber}</td>
                </tr>
                <tr>
                  <td className="text-muted">Address:</td>
                  <td>{order.address}</td>
                </tr>
                <tr>
                  <td className="text-muted">Notes:</td>
                  <td>{order.notes || "No notes"}</td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="col-md-6">
            <h6 className="mb-3">Order Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td className="text-muted" width="40%">Payment Method:</td>
                  <td>{getPaymentMethodBadge(order.paymentmethod)}</td>
                </tr>
                <tr>
                  <td className="text-muted">Delivery Type:</td>
                  <td>
                    <span className={`badge ${order.deliverytype === 'Regular' ? 'bg-primary' : 'bg-warning'}`}>
                      {order.deliverytype}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Inside Dhaka:</td>
                  <td>
                    <span className={`badge ${order.insidedhaka ? 'bg-success' : 'bg-secondary'}`}>
                      {order.insidedhaka ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Prescription:</td>
                  <td>
                    <span className={`badge ${order.prescriptionorder ? 'bg-info' : 'bg-secondary'}`}>
                      {order.prescriptionorder ? 'Required' : 'Not Required'}
                    </span>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>

        <hr />

        <h6 className="mb-3">Order Items</h6>
        <Table bordered>
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {order.orderproductdata?.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>
                  <div className="d-flex align-items-center">
                    <img
                      src={item.image}
                      alt={item.tytle}
                      className="rounded me-2"
                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                    />
                    <div>
                      <div className="fw-semibold">{item.tytle}</div>
                      <small className="text-muted">
                        {item.ptype !== "none" && `${item.ptype} • `}
                        {item.power !== "none" && item.power}
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`badge ${item.type === 'medicine' ? 'bg-danger' : 'bg-info'}`}>
                    {item.type}
                  </span>
                </td>
                <td>{item.quantity}</td>
                <td>{formatCurrency(item.discountPrice || item.regularPrice)}</td>
                <td className="fw-semibold">{formatCurrency(item.totalprice)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="5" className="text-end fw-semibold">Subtotal:</td>
              <td className="fw-semibold">{formatCurrency(order.totalproductamount)}</td>
            </tr>
            <tr>
              <td colSpan="5" className="text-end fw-semibold">Delivery Charge:</td>
              <td className="fw-semibold">{formatCurrency(order.deliverycharge)}</td>
            </tr>
            <tr>
              <td colSpan="5" className="text-end fw-semibold">Total Amount:</td>
              <td className="fw-semibold text-primary">{formatCurrency(order.totalamount)}</td>
            </tr>
          </tfoot>
        </Table>

        <div className="row mt-4">
          <div className="col-md-6">
            <h6>Payment Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td className="text-muted">Payment Status:</td>
                  <td>
                    <span className={`badge ${order.paymentstatus === 'paid' ? 'bg-success' : 'bg-danger'}`}>
                      {order.paymentstatus}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Paid Amount:</td>
                  <td>{formatCurrency(order.paidamount)}</td>
                </tr>
                <tr>
                  <td className="text-muted">Due Amount:</td>
                  <td>{formatCurrency(order.dueamount)}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          <div className="col-md-6">
            <h6>Delivery Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td className="text-muted">Delivery Status:</td>
                  <td>
                    {order.deliverystatusdate ? (
                      <span className="badge bg-success">
                        Delivered on {formatDate(order.deliverystatusdate)} at {formatTime(order.deliverystatustime)}
                      </span>
                    ) : (
                      <span className="badge bg-warning">Pending Delivery</span>
                    )}
                  </td>
                </tr>
                {order.deliverymanname && (
                  <tr>
                    <td className="text-muted">Delivery Man:</td>
                    <td>{order.deliverymanname} ({order.deliverymannumber})</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default OrderDetailsModal;